import { useState } from "react";
import LegalLayout, { Section } from "../components/LegalLayout";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import api, { errorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, MessageSquare } from "lucide-react";

const CATEGORIES = ["bug", "billing", "feature", "account", "other"];

export default function Support() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    category: "other",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSend = form.name.trim() && form.email.trim() && form.subject.trim() && form.message.trim().length >= 5;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSend || sending) return;
    setSending(true);
    try {
      await api.post("/support/contact", form);
      setDone(true);
      toast.success(t("support_success"));
    } catch (err) {
      toast.error(errorMessage(err, t) || t("support_err"));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <LegalLayout title={t("support_title")} updated="February 2026">
        <div className="flex flex-col items-center text-center py-10" data-testid="support-success">
          <div className="rounded-full bg-emerald-500/15 p-4 mb-5">
            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="font-black text-3xl mb-3">{t("support_success")}</h2>
          <p className="text-zinc-400 max-w-md">{t("support_success_sub")}</p>
        </div>
      </LegalLayout>
    );
  }

  return (
    <LegalLayout title={t("support_title")} updated="February 2026">
      <p className="text-zinc-400">{t("support_subtitle")}</p>
      {!user && (
        <p className="text-xs text-amber-400/80 bg-amber-500/[0.06] border border-amber-500/20 rounded-md px-4 py-3">
          {t("support_hint_logged_out")}
        </p>
      )}
      <Section heading={<span className="inline-flex items-center gap-2"><MessageSquare className="w-5 h-5 text-orange-400" /> {t("support")}</span>}>
        <form onSubmit={submit} className="space-y-5" data-testid="support-form">
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-zinc-400 font-bold mb-1.5 block">{t("support_name")} *</span>
              <Input data-testid="support-name" value={form.name} onChange={(e) => set("name", e.target.value)} required className="bg-[#121218] border-white/10 focus:ring-orange-500" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-zinc-400 font-bold mb-1.5 block">{t("support_email")} *</span>
              <Input data-testid="support-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="bg-[#121218] border-white/10 focus:ring-orange-500" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-zinc-400 font-bold mb-1.5 block">{t("support_category")}</span>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="support-category" className="bg-[#121218] border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#16161D] border-white/10">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{t(`support_cat_${c}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-zinc-400 font-bold mb-1.5 block">{t("support_subject")} *</span>
            <Input data-testid="support-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} required maxLength={200} className="bg-[#121218] border-white/10 focus:ring-orange-500" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-zinc-400 font-bold mb-1.5 block">{t("support_message")} *</span>
            <Textarea data-testid="support-message" rows={6} value={form.message} onChange={(e) => set("message", e.target.value)} required maxLength={5000} className="bg-[#121218] border-white/10 focus:ring-orange-500" />
          </label>
          <Button type="submit" data-testid="support-send" disabled={!canSend || sending} className="w-full md:w-auto rounded-full h-11 px-8 bg-orange-500 hover:bg-orange-600 text-black font-black">
            {sending ? "..." : t("support_send")}
          </Button>
        </form>
      </Section>
    </LegalLayout>
  );
}
